import { createReference, getReferenceString, ProfileResource } from "@medplum/core";
import {
  BundleEntry,
  Condition,
  Extension,
  Identifier,
  Procedure,
  Reference,
} from "@medplum/fhirtypes";

import { v4 as uuidv4 } from "uuid";

import { Extensions } from "@/fhir/enums/extensions";
import { joinReferenceString } from "@/utils/medplum";

import {
  EncounterProceduresScope,
  PROCEDURES_ENTERED_IN_ERROR,
  ProceduresPickerSystems,
} from "../constants/procedures";
import {
  ProcedureOwnership,
  ProceduresBundleContext,
  ProceduresWriteScope,
} from "../types/procedures";
import { ProceduresPickerFormType } from "../zod-schemas/procedures";

import { withoutProblemListCategories } from "./diagnosis-problem-list";
import {
  activeClinicalStatus,
  buildConditionCategories,
  buildProcedureCategory,
} from "./procedure-categories";

export type EncounterProceduresData = {
  procedures: Procedure[];
  conditions: Condition[];
};

/**
 * The one place the bundle's encounter-wide context is assembled. The write path and the procedures
 * tab's preview both build a bundle from the same staged sessions, so they have to hand the builder
 * the same references or what the tab shows and what the encounter saves come apart.
 */
export const buildProceduresBundleContext = ({
  profile,
  clientId,
  encounterId,
  isBiller,
  scope,
}: {
  profile: ProfileResource | undefined;
  clientId: string;
  encounterId: string;
  isBiller: boolean;
  /** Which side of the encounter's resources this bundle writes. */
  scope: ProceduresWriteScope;
}): ProceduresBundleContext => ({
  patientReference: { reference: joinReferenceString("Patient", clientId) },
  encounterReference: { reference: joinReferenceString("Encounter", encounterId) },
  recorderReference:
    profile?.resourceType === "Practitioner" ? createReference(profile) : undefined,
  isBiller,
  // The one place the scope becomes a category, so a bundle cannot be written into one side of the
  // encounter carrying the other side's marking.
  isNonClinical: scope === EncounterProceduresScope.NonClinical,
});

type BuildEncounterProceduresBundleParams = ProceduresBundleContext & {
  formValues: ProceduresPickerFormType;
  initialValues: ProceduresPickerFormType;
  existingProcedures: Procedure[];
  existingConditions: Condition[];
  /**
   * How this write identifies the procedures it owns. Left off by a write that owns every procedure
   * in its scope, which is what the procedures tab and the biller section's Add More both do.
   */
  ownership?: ProcedureOwnership;
};

type FormCondition = ProceduresPickerFormType["conditions"][number];
type FormProcedure = ProceduresPickerFormType["procedures"][number];
type ReasonReferences = NonNullable<Procedure["reasonReference"]>;

// ---------------------------------------------------------------------------
// Extension helpers
// ---------------------------------------------------------------------------

const buildProcedureExtensions = (
  procedure: ProceduresPickerFormType["procedures"][number],
): Extension[] => {
  const extensions: Extension[] = [];

  // Always written, including a units value of 0: the form field is a required number, so 0 is
  // the biller's explicit choice, not an absence of one. Dropping it here would make the charge
  // captured from this Procedure fall back to the default of 1 instead of the entered 0.
  extensions.push({ url: Extensions.ProcedureUnits, valueInteger: procedure.units });

  // A single Procedure can carry more than one modifier, so each is stored as its own
  // repeated extension rather than a single multi-valued entry.
  for (const modifier of procedure.modifiers ?? []) {
    if (modifier) extensions.push({ url: Extensions.ProcedureModifiers, valueString: modifier });
  }

  if (procedure.billerNotes) {
    extensions.push({ url: Extensions.ProcedureBillerNotes, valueString: procedure.billerNotes });
  }

  if (procedure.providerNotes) {
    extensions.push({
      url: Extensions.ProcedureProviderNotes,
      valueString: procedure.providerNotes,
    });
  }

  return extensions;
};

// FHIR invariant con-5: a Condition carrying this verification status SHALL NOT also carry a
// clinicalStatus. Every retirement path therefore replaces the clinical status rather than adding
// alongside it, or the server rejects the whole bundle.
const enteredInErrorVerificationStatus: Condition["verificationStatus"] = {
  coding: [
    {
      system: ProceduresPickerSystems.ConditionVerificationStatus,
      code: PROCEDURES_ENTERED_IN_ERROR,
    },
  ],
};

// ---------------------------------------------------------------------------
// Matching helpers
// ---------------------------------------------------------------------------

const matchesCode = (resource: Procedure | Condition, code: string | undefined): boolean =>
  Boolean(code) && Boolean(resource.code?.coding?.some((coding) => coding.code === code));

const isInScope = (resource: Procedure | Condition, codes: Set<string>): boolean =>
  Boolean(resource.code?.coding?.some((coding) => coding.code && codes.has(coding.code)));

const toCodeSet = (formEntries: { code: string }[]): Set<string> =>
  new Set(formEntries.map(({ code }) => code).filter(Boolean));

const toReferenceStringSet = (references: Reference[]): Set<string> =>
  new Set(
    references
      .map(({ reference }) => reference)
      .filter((reference): reference is string => Boolean(reference)),
  );

/** Whether a procedure carries the given key, whatever else it is identified by. */
const hasIdentifier = (procedure: Procedure, { system, value }: Identifier): boolean =>
  Boolean(procedure.identifier?.some((item) => item.system === system && item.value === value));

/** Whether a procedure is already claimed under the given key's system, whoever claimed it. */
const isClaimed = (procedure: Procedure, { system }: Identifier): boolean =>
  Boolean(procedure.identifier?.some((item) => item.system === system));

/**
 * The procedures a write owns, out of everything its scope holds.
 *
 * Its own are the ones carrying its key. A procedure carrying nobody's key that the caller named as
 * adoptable is taken over, which is how a row written before the key existed keeps the Procedure it
 * is already billed under instead of gaining a second one. A procedure claimed under another key
 * belongs to another row and is never owned, however it is coded.
 */
const selectOwnedProcedures = (
  procedures: Procedure[],
  { identifier, adoptableReferences = [] }: ProcedureOwnership,
): Procedure[] => {
  const adoptable = new Set(adoptableReferences);

  return procedures.filter((procedure) => {
    if (hasIdentifier(procedure, identifier)) return true;
    if (isClaimed(procedure, identifier)) return false;

    const reference = getReferenceString(procedure);

    return Boolean(reference) && adoptable.has(reference!);
  });
};

/**
 * The identifiers a written procedure carries: the ones it already had, with this write's key in
 * place of any other key from the same system. A procedure belongs to one row, so two row keys on
 * one resource would leave its owner ambiguous.
 */
const withOwnerIdentifier = (
  current: Identifier[] | undefined,
  owner: Identifier | undefined,
): Identifier[] =>
  owner
    ? [...(current ?? []).filter(({ system }) => system !== owner.system), owner]
    : (current ?? []);

/** Ids of the encounter conditions the picker's selection already maps to. */
const toMatchedConditionIds = (sessionConditions: SessionCondition[]): Set<string> =>
  new Set(
    sessionConditions.map(({ existing }) => existing?.id).filter((id): id is string => Boolean(id)),
  );

// Adds the picker's condition references to whatever the procedure already points at, so linking a
// procedure to a second diagnosis never drops the first one.
const addReasonReferences = (
  current: ReasonReferences | undefined,
  added: ReasonReferences,
): ReasonReferences => {
  const merged: ReasonReferences = [...(current ?? [])];

  for (const reference of added) {
    if (!merged.some((item) => item.reference === reference.reference)) merged.push(reference);
  }

  return merged;
};

// Drops the given diagnoses from a procedure's reasonReference, along with any entry that carries no
// reference string: the picker links procedures to diagnoses by reference alone, so an entry without
// one can never be matched, unlinked, or counted as a live link.
const removeReasonReferences = (
  current: ReasonReferences | undefined,
  removed: Set<string>,
): ReasonReferences =>
  (current ?? []).filter(({ reference }) => !!reference && !removed.has(reference));

// A dropped diagnosis stays alive as long as one procedure still points at it and has not been
// retired.
const hasLiveProcedure = (
  procedures: Procedure[],
  conditionReference: string | undefined,
): boolean =>
  Boolean(conditionReference) &&
  procedures.some(
    (procedure) =>
      procedure.status !== PROCEDURES_ENTERED_IN_ERROR &&
      procedure.reasonReference?.some(({ reference }) => reference === conditionReference),
  );

// ---------------------------------------------------------------------------
// Conditions selected in the picker
// ---------------------------------------------------------------------------

/** The picker's conditions paired with the encounter resource each one maps to, if any. */
type SessionCondition = {
  formCondition: FormCondition;
  existing?: Condition;
  fullUrl?: string;
  reference: Reference<Condition>;
};

// Pairs every diagnosis selected in the picker with the encounter Condition carrying the same code.
// A diagnosis the encounter does not have yet gets a urn:uuid, so the procedures in the same
// transaction bundle can reference it before it has an id.
const matchSelectedConditions = (
  formConditions: FormCondition[],
  existingConditions: Condition[],
): SessionCondition[] => {
  const sessionConditions: SessionCondition[] = [];

  for (const formCondition of formConditions) {
    if (!formCondition.code) continue;

    const existing = existingConditions.find((item) => matchesCode(item, formCondition.code));
    const fullUrl = existing?.id ? undefined : `urn:uuid:${uuidv4()}`;

    sessionConditions.push({
      formCondition,
      existing,
      fullUrl,
      reference: existing?.id ? createReference(existing) : { reference: fullUrl },
    });
  }

  return sessionConditions;
};

// ---------------------------------------------------------------------------
// Resource builders
// ---------------------------------------------------------------------------

// Rebuilds a procedure from what the picker holds: its units, modifiers, and notes come from the
// form, so this both creates procedures and overwrites the ones this session loaded.
const buildProcedureResource = ({
  formProcedure,
  existing,
  reasonReference,
  context,
}: {
  formProcedure: FormProcedure;
  existing?: Procedure;
  reasonReference: ReasonReferences;
  context: ProceduresBundleContext;
}): Procedure => {
  const { patientReference, encounterReference, recorderReference, isBiller, isNonClinical } =
    context;
  const extensions = buildProcedureExtensions(formProcedure);

  return {
    ...(existing?.id ? { id: existing.id } : {}),
    resourceType: "Procedure",
    status: "completed",
    // A rebuild replaces what the picker holds, and a procedure's identifiers are not that: they
    // are how other records find it again.
    ...(existing?.identifier?.length ? { identifier: existing.identifier } : {}),
    subject: patientReference,
    category: buildProcedureCategory({ isBiller, isNonClinical }),
    code: {
      coding: [
        { system: formProcedure.system, code: formProcedure.code, display: formProcedure.display },
      ],
    },
    encounter: encounterReference,
    ...(recorderReference ? { recorder: recorderReference } : {}),
    ...(reasonReference.length ? { reasonReference } : {}),
    ...(extensions.length ? { extension: extensions } : {}),
  };
};

// A selected diagnosis is always live, so it carries the clinical status alone (con-5). Retiring one
// is the dropped-diagnosis path's job.
const buildConditionResource = ({
  formCondition,
  existing,
  context,
}: {
  formCondition: FormCondition;
  existing?: Condition;
  context: ProceduresBundleContext;
}): Condition => {
  const { patientReference, encounterReference, recorderReference, isBiller, isNonClinical } =
    context;

  return {
    ...(existing?.id ? { id: existing.id } : {}),
    resourceType: "Condition",
    subject: patientReference,
    encounter: encounterReference,
    ...(recorderReference ? { recorder: recorderReference } : {}),
    category: buildConditionCategories({ isBiller, isNonClinical, existing }),
    clinicalStatus: activeClinicalStatus,
    code: {
      coding: [
        { system: formCondition.system, code: formCondition.code, display: formCondition.display },
      ],
    },
  };
};

// Drops this session's diagnoses from a procedure's reasonReference. Losing the last one retires the
// procedure as entered-in-error; losing one of several only unlinks it.
const unlinkOrRetireProcedure = (
  existing: Procedure,
  sessionConditionRefs: Set<string>,
): Procedure => {
  const remainingReasonReference = removeReasonReferences(
    existing.reasonReference,
    sessionConditionRefs,
  );

  if (remainingReasonReference.length) {
    return { ...existing, reasonReference: remainingReasonReference };
  }

  const { reasonReference: _unlinked, ...withoutReasonReference } = existing;

  return { ...withoutReasonReference, status: PROCEDURES_ENTERED_IN_ERROR };
};

// ---------------------------------------------------------------------------
// Procedure reconciliation
// ---------------------------------------------------------------------------

type ProcedureReconciliation = {
  entries: BundleEntry[];
  /**
   * Every live procedure the encounter ends up with: the ones this bundle writes plus the ones it
   * never touches. Deciding whether a diagnosis still has anything pointing at it needs both.
   */
  resultingProcedures: Procedure[];
};

// Reconciles the picker's procedures against the encounter's. The selected ones are written linked to
// every selected diagnosis; the in-scope ones the picker no longer has selected are unlinked from the
// diagnoses this save reshaped, and retired instead of deleted once nothing is left pointing at them.
//
// Only the procedures this write owns are matched and rewritten. `ownedProcedures` is every procedure
// in the scope unless the caller narrowed it, so a write that owns one row's procedure leaves the
// procedure another row is billed under alone even when the two carry the same code.
const reconcileProcedures = ({
  formProcedures,
  existingProcedures,
  ownedProcedures,
  initialProcedureCodes,
  selectedConditionRefs,
  droppedConditionRefs,
  ownerIdentifier,
  context,
}: {
  formProcedures: FormProcedure[];
  existingProcedures: Procedure[];
  ownedProcedures: Procedure[];
  initialProcedureCodes: Set<string>;
  selectedConditionRefs: Reference<Condition>[];
  droppedConditionRefs: Set<string>;
  /** Stamped on every procedure this write touches, so the next one finds the same resource. */
  ownerIdentifier?: Identifier;
  context: ProceduresBundleContext;
}): ProcedureReconciliation => {
  const entries: BundleEntry[] = [];
  const resultingProcedures: Procedure[] = [];
  const selectedProcedureIds = new Set<string>();
  const ownedProcedureIds = new Set(ownedProcedures.map(({ id }) => id).filter(Boolean));

  // Carries the write's key onto the resource, whether it was rebuilt or kept as it is stored.
  const claim = (procedure: Procedure): Procedure => {
    const identifier = withOwnerIdentifier(procedure.identifier, ownerIdentifier);

    return identifier.length ? { ...procedure, identifier } : procedure;
  };

  // The diagnoses this save is reshaping. Dropping a procedure unlinks it from these and only these,
  // never from the diagnoses another picker session attached to the same procedure.
  const sessionConditionRefs = new Set([
    ...toReferenceStringSet(selectedConditionRefs),
    ...droppedConditionRefs,
  ]);

  for (const formProcedure of formProcedures) {
    if (!formProcedure.code) continue;

    const existing = ownedProcedures.find((item) => matchesCode(item, formProcedure.code));
    if (existing?.id) selectedProcedureIds.add(existing.id);

    // A kept procedure keeps the diagnoses it was already linked to, minus the ones this save drops:
    // a procedure must never point at a diagnosis this same bundle retires.
    const reasonReference = addReasonReferences(
      removeReasonReferences(existing?.reasonReference, droppedConditionRefs),
      selectedConditionRefs,
    );

    // Already on the encounter under a diagnosis this picker never loaded: link it and leave the
    // rest of the resource alone. Its units, modifiers, and notes belong to the diagnosis that
    // recorded them, and the picker seeded blank values for it.
    const resource = claim(
      existing?.id && !initialProcedureCodes.has(formProcedure.code)
        ? { ...existing, reasonReference }
        : buildProcedureResource({ formProcedure, existing, reasonReference, context }),
    );

    resultingProcedures.push(resource);
    entries.push({
      resource,
      request: {
        method: existing?.id ? "PUT" : "POST",
        url: existing?.id ? getReferenceString(existing)! : "Procedure",
      },
    });
  }

  for (const existing of existingProcedures) {
    if (existing.id && selectedProcedureIds.has(existing.id)) continue;

    // Not this write's to reshape: another row's procedure, or one outside the picker's initial
    // scope. It still counts when the conditions below check whether a diagnosis has a live
    // procedure left, which is what keeps another row's diagnosis off this save's retirement list.
    if (
      !existing.id ||
      !ownedProcedureIds.has(existing.id) ||
      !isInScope(existing, initialProcedureCodes)
    ) {
      resultingProcedures.push(existing);
      continue;
    }

    const updatedProcedure = unlinkOrRetireProcedure(existing, sessionConditionRefs);

    resultingProcedures.push(updatedProcedure);
    entries.push({
      resource: updatedProcedure,
      request: { method: "PUT", url: getReferenceString(existing)! },
    });
  }

  return { entries, resultingProcedures };
};

// ---------------------------------------------------------------------------
// Condition entries
// ---------------------------------------------------------------------------

// Writes the diagnoses selected in the picker. A diagnosis can be recorded with no procedure under it,
// so a selected one stays live whatever the procedures around it do.
const buildSelectedConditionEntries = ({
  sessionConditions,
  context,
}: {
  sessionConditions: SessionCondition[];
  context: ProceduresBundleContext;
}): BundleEntry[] =>
  sessionConditions.map(({ formCondition, existing, fullUrl }) => ({
    ...(fullUrl ? { fullUrl } : {}),
    resource: buildConditionResource({ formCondition, existing, context }),
    request: {
      method: existing?.id ? "PUT" : "POST",
      url: existing?.id ? getReferenceString(existing)! : "Condition",
    },
  }));

/** The encounter diagnoses the picker loaded but no longer has selected. */
const findDroppedConditions = ({
  existingConditions,
  initialConditionCodes,
  sessionConditions,
}: {
  existingConditions: Condition[];
  initialConditionCodes: Set<string>;
  sessionConditions: SessionCondition[];
}): Condition[] => {
  const selectedConditionIds = toMatchedConditionIds(sessionConditions);

  return existingConditions.filter(
    (condition) =>
      condition.id &&
      !selectedConditionIds.has(condition.id) &&
      isInScope(condition, initialConditionCodes),
  );
};

// Retires the diagnoses the picker dropped. A dropped diagnosis is unlinked from every procedure this
// save writes, so anything still pointing at it is a procedure outside the picker's scope: another
// session's work, which keeps the diagnosis alive under the same rule the selected ones follow.
const buildDroppedConditionEntries = ({
  droppedConditions,
  resultingProcedures,
}: {
  droppedConditions: Condition[];
  resultingProcedures: Procedure[];
}): BundleEntry[] =>
  droppedConditions
    .filter((condition) => !hasLiveProcedure(resultingProcedures, getReferenceString(condition)))
    .map((condition) => {
      // Dropping the clinical status the encounter recorded, so the retired diagnosis satisfies
      // con-5 and keeps everything else it was stored with — everything except the problem-list
      // membership, which a diagnosis entered in error has no business keeping. This path leaves the
      // stored resource as it is rather than rebuilding it, so the membership has to be taken off
      // here.
      const { clinicalStatus: _retired, ...withoutClinicalStatus } = condition;

      return {
        resource: {
          ...withoutProblemListCategories(withoutClinicalStatus),
          verificationStatus: enteredInErrorVerificationStatus,
        },
        request: { method: "PUT" as const, url: getReferenceString(condition)! },
      };
    });

// ---------------------------------------------------------------------------
// Bundle builder
// ---------------------------------------------------------------------------

// Reconciles the picker's conditions and procedures against what already exists on the encounter.
//
// Reconciliation is scoped to the resources the picker was opened with (initialValues), so a blank
// "New" picker adds resources without touching the rest of the encounter and an "Edit" picker only
// reshapes the condition it loaded.
//
// Procedures are linked to conditions through reasonReference: a procedure selected in the picker
// gets every selected condition added to its own reasonReference, keeping the diagnoses it was
// already linked to. A selected condition stays live even with no procedure under it. Removing a
// code never deletes a resource, it is unlinked and, once nothing points at it, retired as
// entered-in-error.
//
// A write that owns its procedures one at a time passes `ownership`, and then only the procedures
// carrying its key are matched by code and rewritten. Everything else in the scope is left as it is
// and only counted when deciding whether a diagnosis still has a live procedure pointing at it. That
// is what a biller-section row edit needs: two of its rows can be billed under the same procedure
// code, and without it an edit to either would rewrite whichever Procedure that code resolved to.
//
// Condition entries are emitted before Procedure entries so urn:uuid references resolve within the
// same transaction bundle, even though the conditions are built last.
export const buildEncounterProceduresBundle = ({
  formValues,
  initialValues,
  existingProcedures,
  existingConditions,
  ownership,
  ...context
}: BuildEncounterProceduresBundleParams): BundleEntry[] => {
  // Only resources that were loaded into the picker are eligible for removal.
  const initialConditionCodes = toCodeSet(initialValues.conditions);
  const initialProcedureCodes = toCodeSet(initialValues.procedures);

  // A write without ownership owns every procedure its scope holds, which is how the procedures tab
  // and Add More both work: one procedure per code, matched by that code.
  const ownedProcedures = ownership
    ? selectOwnedProcedures(existingProcedures, ownership)
    : existingProcedures;

  const sessionConditions = matchSelectedConditions(formValues.conditions, existingConditions);
  const selectedConditionRefs = sessionConditions.map(({ reference }) => reference);

  const droppedConditions = findDroppedConditions({
    existingConditions,
    initialConditionCodes,
    sessionConditions,
  });
  const droppedConditionRefs = toReferenceStringSet(
    droppedConditions.map((condition) => createReference(condition)),
  );

  const { entries: procedureEntries, resultingProcedures } = reconcileProcedures({
    formProcedures: formValues.procedures,
    existingProcedures,
    ownedProcedures,
    initialProcedureCodes,
    selectedConditionRefs,
    droppedConditionRefs,
    ownerIdentifier: ownership?.identifier,
    context,
  });

  const conditionEntries = [
    ...buildSelectedConditionEntries({ sessionConditions, context }),
    ...buildDroppedConditionEntries({ droppedConditions, resultingProcedures }),
  ];

  return [...conditionEntries, ...procedureEntries];
};
